import React, { createContext, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'

import themes from '../theme'

const ThemeContext = createContext()

// Storage key for theme preference
const THEME_STORAGE_KEY = 'racepack-theme-preference'

// Get initial theme: check localStorage first, then system preference
const getInitialTheme = () => {
  try {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY)
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error)
  }

  // Fallback to system preference
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  // Default to light mode if nothing else works
  return 'light'
}

export const ThemeProvider = ({ children = null }) => {
  const [theme, setThemeState] = useState(getInitialTheme)

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error)
    }
  }, [theme])

  // Listen to system theme changes (optional - only if no user preference is set)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return () => {}
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      // Only update if user hasn't set a preference (optional behavior)
      // For now, we respect user choice and don't auto-update
      // Uncomment below if you want system preference to override on change:
      // try {
      //   const savedTheme = localStorage.getItem(THEME_STORAGE_KEY)
      //   if (!savedTheme) {
      //     setThemeState(e.matches ? 'dark' : 'light')
      //   }
      // } catch (error) {
      //   // Ignore
      // }
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    // Fallback for older browsers
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }
    return () => {}
  }, [])

  // Memoized theme toggle functions for better performance
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])

  const darkTheme = useCallback(() => {
    setThemeState('dark')
  }, [])

  const lightTheme = useCallback(() => {
    setThemeState('light')
  }, [])

  // Get the MUI theme object
  const muiTheme = themes[theme]

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        darkTheme,
        lightTheme,
      }}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
ThemeProvider.propTypes = {
  children: PropTypes.node,
}

ThemeProvider.defaultProps = {
  children: null,
}

export const ThemeConsumer = ThemeContext.Consumer

export default ThemeContext
